import { t as ct } from "./content.js";
import { householdFetch } from "./api.js";
import { apiUrl, publicOrigin } from "./origin.js";

// LB-382 (Alan, 27 Sep). Sharing a practice list with another family.
//
// A COPY, NOT A REFERENCE. He asked first for every instance to update when the publisher edits
// it, and then chose the copy himself — "that is probably safer anyway". It is, and for a reason
// worth writing down: a live reference would let someone in another household change words that
// are already on your child's grid, AFTER you approved them, straight past the rude block that
// stops one being typed in. Approving a shared list means approving those words. The share sheet
// says as much to the person sharing, so nobody is surprised later.

// The link a parent sends. Same shape as the household invite (LB-361) so one landing page
// handles both: a parameter the app reads on open.
export function homeworkShareLink(listCode) {
  return `${publicOrigin()}/?practice=${encodeURIComponent(listCode)}`;
}

// What lands in WhatsApp. The name and the school year travel with it (his 2.3) so a parent can
// see what they are being given before they open anything.
export function homeworkShareText(list, schoolYear) {
  const year = String(schoolYear || "").trim();
  const what = [list.name, ct("hwshare.n_words", "{n} words", { n: (list.pairs || []).length }), year].filter(Boolean).join(" · ");
  return `${ct("hwshare.share_text", "Homework practice for the kids — {what}. It opens in Little Bankers:", { what })}\n${homeworkShareLink(list.code)}`;
}

// Put the snapshot where a link can reach it, then hand it to the PHONE'S OWN SHARE SHEET, so
// WhatsApp, Messages and the class group are wherever the parent already keeps them (Alan,
// 27 Sep: "can we use the sharing sheet with whatsapp etc on it?"). src/lib/referral.js already
// does exactly this for the household invite; this follows it rather than inventing a second
// way, including its rule that a dismissed sheet is not a failure and gets no toast.
// Returns "shared" | "copied" | "dismissed" | { error }.
export async function shareHomeworkList(code, list, schoolYear, { mode = "share" } = {}) {
  const res = await householdFetch(code, "/homework/publish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      code: list.code, name: list.name, type: list.type, lang: list.lang || "", direction: list.direction || "en2lang",
      schoolYear: schoolYear || "", pairs: (list.pairs || []).map((p) => ({ a: p.a, b: p.b || "" })),
    }),
  }).catch(() => null);
  if (!res || !res.ok) {
    // The worker refuses a list with a rude word in its name or rows (LB-382, 1 Oct). The app
    // already blocks a rude ROW before a list can be saved; the name was never checked.
    let rude = false;
    try { rude = !!(res && res.status === 400 && (await res.json()).rude); } catch {}
    return { error: rude ? "rude" : "publish" };
  }
  const text = homeworkShareText(list, schoolYear);
  try {
    if (mode === "share" && typeof navigator !== "undefined" && navigator.share) {
      await navigator.share({ text });
      return "shared";
    }
    await navigator.clipboard.writeText(mode === "share" ? text : homeworkShareLink(list.code));
    return "copied";
  } catch (err) {
    if (err && err.name === "AbortError") return "dismissed";
    return { error: "copy" };
  }
}

// Reading one is public — that is what a link is for. Returns the snapshot or null.
export async function fetchSharedList(listCode) {
  try {
    const res = await fetch(apiUrl(`/api/homework/shared/${encodeURIComponent(String(listCode || "").toUpperCase())}`));
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.ok && data.list ? data.list : null;
  } catch { return null; }
}

// The code sitting in the URL when the app opens, if there is one.
export function sharedCodeFromUrl(search) {
  try {
    const params = new URLSearchParams(search || (typeof window !== "undefined" ? window.location.search : ""));
    const raw = String(params.get("practice") || "").toUpperCase();
    return /^HW-[A-Z0-9]{6}$/.test(raw) ? raw : null;
  } catch { return null; }
}

/* ---------------- LB-382 (Alan, 1 Oct): "Send it to my app" ----------------
 * A link opened on an iPhone lands in the browser, never in the installed app, and the browser
 * is signed out because its storage is not the app's. A push does open the app. So the browser
 * page asks for the email the parent uses here and the worker sends the list to that household.
 */

// Ask the worker to send a shared list to whoever uses Little Bankers with this email. The
// answer is the same whether or not anybody does, by design, so "ok" means "asked", not "found".
// Returns "ok" | "limited" | "error".
export async function sendSharedToApp(listCode, email) {
  try {
    const res = await fetch(apiUrl("/api/homework/send-to-app"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: String(listCode || "").toUpperCase(), email: String(email || "").trim() }),
    });
    if (res.status === 429) return "limited";
    return res.ok ? "ok" : "error";
  } catch { return "error"; }
}

// The email typed on this browser last time, so the next shared list is one tap (his ask: the
// journey for an existing family must be "as slick as possible"). It never leaves this browser
// except in the request above, and it is the visitor's own address.
export const HW_SEND_EMAIL_KEY = "lb_hw_send_email";
export function rememberedSendEmail() {
  try { return String(localStorage.getItem(HW_SEND_EMAIL_KEY) || ""); } catch { return ""; }
}
export function rememberSendEmail(email) {
  try { if (email) localStorage.setItem(HW_SEND_EMAIL_KEY, String(email).trim()); else localStorage.removeItem(HW_SEND_EMAIL_KEY); } catch {}
}
// "a•••@example.com" — enough to recognise your own address, not enough to read over a shoulder.
export function maskEmail(email) {
  const [local, domain] = String(email || "").split("@");
  if (!local || !domain) return "";
  return `${local[0]}•••@${domain}`;
}

// The household's inbox: lists sent to this household's app from a browser and not yet
// collected. Returns list codes, [] on any failure (a preview, a signed-out device, no network).
export async function fetchHomeworkInbox(code) {
  try {
    const res = await householdFetch(code, "/homework/inbox");
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data && data.codes) ? data.codes.filter((c) => /^HW-[A-Z0-9]{6}$/.test(String(c))) : [];
  } catch { return []; }
}

export async function clearHomeworkInbox(code, codes) {
  if (!codes || !codes.length) return;
  try {
    await householdFetch(code, "/homework/inbox", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ codes }) });
  } catch { /* left in the inbox; collecting twice is harmless — a pending copy is not duplicated */ }
}

/* ---------------- LB-382 (Alan, 1 Oct): a family without the app ----------------
 * "The landing page acts as the fork - send to app, get app, or try the homework now." These are
 * the second and third: one practice in the browser, and the list as a way past the invite gate
 * ("let them past the gate").
 */

// Exchange a shared list for a creation ticket — the same ticket an invite code buys. Returns
// { ticket } | { full: true } (the list has let in as many families as it may) | { limited: true }
// | { error: true }.
export async function requestSharedListPass(listCode) {
  try {
    const res = await fetch(apiUrl("/api/homework/pass"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: String(listCode || "").toUpperCase() }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 429) return { limited: true };
    if (res.ok && typeof data.ticket === "string" && data.ticket) return { ticket: data.ticket };
    return data && data.full ? { full: true } : { error: true };
  } catch { return { error: true }; }
}

// The list a new family arrived with, carried across sign-up IN THIS BROWSER: set when they
// are let past the gate, taken by App the first time it opens on the new household, where it
// becomes the same pending request a tapped link makes. Taken means removed.
export const HW_PENDING_LIST_KEY = "lb_hw_pending_list";
export function setPendingSharedCode(listCode) {
  try { if (/^HW-[A-Z0-9]{6}$/.test(String(listCode || ""))) localStorage.setItem(HW_PENDING_LIST_KEY, listCode); } catch {}
}
export function takePendingSharedCode() {
  try {
    const raw = String(localStorage.getItem(HW_PENDING_LIST_KEY) || "").toUpperCase();
    localStorage.removeItem(HW_PENDING_LIST_KEY);
    return /^HW-[A-Z0-9]{6}$/.test(raw) ? raw : null;
  } catch { return null; }
}

// ONE practice per list on a browser with no account (Alan: "They can practice once and see
// results but can't restart or anything"). What is kept is the result of that one go, so the
// page can show it again instead of offering another. It is per browser and a parent clearing
// their browser data gets another go; that is not worth policing.
export const HW_TRIED_KEY_PREFIX = "lb_hw_tried:";
export function readGuestResult(listCode) {
  try {
    const r = JSON.parse(localStorage.getItem(HW_TRIED_KEY_PREFIX + listCode) || "null");
    return r && Number.isFinite(r.right) && Number.isFinite(r.asked) && Array.isArray(r.misses) ? r : null;
  } catch { return null; }
}
export function clearGuestResult(listCode) {
  try { localStorage.removeItem(HW_TRIED_KEY_PREFIX + listCode); } catch {}
}
export function saveGuestResult(listCode, result) {
  try {
    localStorage.setItem(HW_TRIED_KEY_PREFIX + listCode, JSON.stringify({
      right: result.right, asked: result.asked,
      misses: (result.misses || []).slice(0, 60).map((m) => ({ q: String(m.q || ""), answer: String(m.answer || ""), given: String(m.given || "") })),
      // The ones the child got right, so the results can lead with them (Alan, 1 Oct: "Is it
      // demotivating to only show the words they got wrong?"). Absent on a result saved before this.
      rights: (result.rights || []).slice(0, 60).map((m) => String(m || "")),
    }));
  } catch {}
}
